// Utilisateurs.js

import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  IconButton,
  TextField,
  MenuItem
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import axios from 'axios';
import apiUrl from '../config';
import LayoutDashboard from '../layout/LayoutDashboard';
import "../CSS/Utilisateurs.css";

const USERS_CACHE_KEY = "utilisateurs_admin_cache_v4";

const getCachedUsers = () => {
  try {
    const cached = sessionStorage.getItem(USERS_CACHE_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch {
    sessionStorage.removeItem(USERS_CACHE_KEY);
    return [];
  }
};

const saveUsersCache = (data) => {
  sessionStorage.setItem(USERS_CACHE_KEY, JSON.stringify(data));
};

const normalizeText = (value) => {
  if (!value) return "";

  return value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

const isAccountValid = (user) => {
  return user?.compte_status === "Compte validé";
};

const isUserActive = (user) => {
  return Number(user?.actif ?? 1) === 1;
};

const getRoleLabel = (role) => {
  if (!role) return "—";

  const value = role.toString().toLowerCase();

  if (value === "admin") return "Admin";
  if (value === "cdi") return "CDI";
  if (value === "user") return "Utilisateur";

  return role;
};

const Utilisateurs = () => {
  const [users, setUsers] = useState(() => getCachedUsers());
  const [loading, setLoading] = useState(() => getCachedUsers().length === 0);

  const [openDialog, setOpenDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [openAddUser, setOpenAddUser] = useState(false);

  const [openEditUser, setOpenEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editNom, setEditNom] = useState('');
  const [editPrenom, setEditPrenom] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('CDI');

  const [openActiveDialog, setOpenActiveDialog] = useState(false);
  const [userToToggle, setUserToToggle] = useState(null);

  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('CDI');

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Tous');
  const [filterRole, setFilterRole] = useState('Tous');

  const [creatingUser, setCreatingUser] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [togglingUser, setTogglingUser] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(null);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const updateUsersState = (data) => {
    const safeData = Array.isArray(data) ? data : [];
    setUsers(safeData);
    saveUsersCache(safeData);
  };

  const fetchUsers = async (silent = false) => {
    if (!silent && users.length === 0) {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem('token');

      const response = await axios.get(
        `${apiUrl}/users-admin?fresh=1&t=${Date.now()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      updateUsersState(response.data);

    } catch (error) {
      console.error(error);

      setNotification({
        open: true,
        message: "Erreur lors du chargement des utilisateurs.",
        severity: "error"
      });

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    let data = [...users];

    if (searchTerm.trim() !== '') {
      const term = normalizeText(searchTerm);

      data = data.filter((user) => {
        const content = normalizeText(`
          ${user.nom || ''}
          ${user.prenom || ''}
          ${user.email || ''}
          ${user.compte_status || ''}
          ${user.statut_acces || ''}
          ${user.role || user.type || user.Type || ''}
        `);

        return content.includes(term);
      });
    }

    if (filterStatus === "valides") {
      data = data.filter((user) => isAccountValid(user));
    }

    if (filterStatus === "non_valides") {
      data = data.filter((user) => !isAccountValid(user));
    }

    if (filterStatus === "actifs") {
      data = data.filter((user) => isUserActive(user));
    }

    if (filterStatus === "desactives") {
      data = data.filter((user) => !isUserActive(user));
    }

    if (filterRole !== "Tous") {
      data = data.filter((user) => {
        const userRole = normalizeText(user.role || user.type || user.Type || '');
        return userRole === normalizeText(filterRole);
      });
    }

    return data;
  }, [users, searchTerm, filterStatus, filterRole]);

  const totalUsers = users.length;
  const totalValid = users.filter((user) => isAccountValid(user)).length;
  const totalPending = users.filter((user) => !isAccountValid(user)).length;
  const totalActive = users.filter((user) => isUserActive(user)).length;
  const totalDisabled = users.filter((user) => !isUserActive(user)).length;

  const totalAdmins = users.filter((user) => {
    const userRole = normalizeText(user.role || user.type || user.Type || '');
    return userRole === "admin";
  }).length;

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('Tous');
    setFilterRole('Tous');
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setOpenDialog(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditNom(user.nom || '');
    setEditPrenom(user.prenom || '');
    setEditEmail(user.email || '');
    setEditRole(user.role || user.type || user.Type || 'CDI');
    setOpenEditUser(true);
  };

  const openToggleDialog = (user) => {
    setUserToToggle(user);
    setOpenActiveDialog(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete?.email) return;

    setDeletingUser(true);

    try {
      const token = localStorage.getItem('token');

      await axios.delete(
        `${apiUrl}/delete-user?email=${encodeURIComponent(userToDelete.email)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedUsers = users.filter(u => u.email !== userToDelete.email);

      updateUsersState(updatedUsers);

      setNotification({
        open: true,
        message: "Utilisateur supprimé avec succès.",
        severity: "success"
      });

      setOpenDialog(false);
      setUserToDelete(null);

    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || "Erreur lors de la suppression.",
        severity: "error"
      });

    } finally {
      setDeletingUser(false);
    }
  };

  const handleAddUser = async () => {
    if (!nom.trim() || !prenom.trim() || !email.trim()) {
      setNotification({
        open: true,
        message: "Tous les champs sont obligatoires.",
        severity: "error"
      });
      return;
    }

    setCreatingUser(true);

    try {
      const token = localStorage.getItem('token');

      await axios.post(
        `${apiUrl}/register`,
        {
          nom: nom.trim(),
          prenom: prenom.trim(),
          email: email.trim().toLowerCase(),
          role
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotification({
        open: true,
        message: "Utilisateur ajouté. L'email de création du mot de passe a été envoyé.",
        severity: "success"
      });

      setOpenAddUser(false);

      setNom('');
      setPrenom('');
      setEmail('');
      setRole('CDI');

      await fetchUsers(true);

    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || "Erreur lors de la création de l'utilisateur.",
        severity: "error"
      });

    } finally {
      setCreatingUser(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser?.id) return;

    if (!editNom.trim() || !editPrenom.trim() || !editEmail.trim() || !editRole) {
      setNotification({
        open: true,
        message: "Tous les champs sont obligatoires.",
        severity: "error"
      });
      return;
    }

    setUpdatingUser(true);

    try {
      const token = localStorage.getItem('token');

      await axios.put(
        `${apiUrl}/users-admin/${editingUser.id}`,
        {
          nom: editNom.trim(),
          prenom: editPrenom.trim(),
          email: editEmail.trim().toLowerCase(),
          role: editRole
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedUsers = users.map((user) => {
        if (String(user.id) !== String(editingUser.id)) {
          return user;
        }

        return {
          ...user,
          nom: editNom.trim(),
          prenom: editPrenom.trim(),
          email: editEmail.trim().toLowerCase(),
          role: editRole,
          type: editRole
        };
      });

      updateUsersState(updatedUsers);

      setNotification({
        open: true,
        message: "Utilisateur modifié avec succès.",
        severity: "success"
      });

      setOpenEditUser(false);
      setEditingUser(null);

      await fetchUsers(true);

    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || "Erreur lors de la modification.",
        severity: "error"
      });

    } finally {
      setUpdatingUser(false);
    }
  };

  const handleToggleUserActive = async () => {
    if (!userToToggle?.id) return;

    const nextActif = isUserActive(userToToggle) ? 0 : 1;

    setTogglingUser(true);

    try {
      const token = localStorage.getItem('token');

      await axios.put(
        `${apiUrl}/users-admin/${userToToggle.id}/active`,
        { actif: nextActif },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedUsers = users.map((user) => {
        if (String(user.id) !== String(userToToggle.id)) {
          return user;
        }

        return {
          ...user,
          actif: nextActif,
          statut_acces: nextActif === 1 ? "Actif" : "Désactivé"
        };
      });

      updateUsersState(updatedUsers);

      setNotification({
        open: true,
        message: nextActif === 1
          ? "Compte réactivé avec succès."
          : "Compte désactivé avec succès.",
        severity: "success"
      });

      setOpenActiveDialog(false);
      setUserToToggle(null);

      await fetchUsers(true);

    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || "Erreur lors du changement de statut.",
        severity: "error"
      });

    } finally {
      setTogglingUser(false);
    }
  };

  const handleResendPasswordEmail = async (user) => {
    if (!user?.email) return;

    setResendingEmail(user.email);

    try {
      const token = localStorage.getItem('token');

      await axios.post(
        `${apiUrl}/resend-password-email`,
        { email: user.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotification({
        open: true,
        message: `Email renvoyé à ${user.email}.`,
        severity: "success"
      });

    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || "Erreur lors de l'envoi de l'email.",
        severity: "error"
      });

    } finally {
      setResendingEmail(null);
    }
  };

  return (
    <LayoutDashboard>

      <div className="utilisateurs-page">

        <section className="utilisateurs-hero">
          <div>
            <span className="utilisateurs-badge">Administration</span>
            <h1>Gestion des utilisateurs</h1>
            <p>
              Ajoutez, modifiez, désactivez, réactivez ou supprimez les utilisateurs de la plateforme Biam Consulting.
            </p>
          </div>
        </section>

        <section className="utilisateurs-kpi-grid">

          <div className="utilisateurs-kpi-card">
            <span>Total utilisateurs</span>
            <strong>{totalUsers}</strong>
          </div>

          <div className="utilisateurs-kpi-card">
            <span>Comptes validés</span>
            <strong className="kpi-green">{totalValid}</strong>
          </div>

          <div className="utilisateurs-kpi-card">
            <span>En attente</span>
            <strong className="kpi-orange">{totalPending}</strong>
          </div>

          <div className="utilisateurs-kpi-card">
            <span>Désactivés</span>
            <strong className="kpi-red">{totalDisabled}</strong>
          </div>

          <div className="utilisateurs-kpi-card">
            <span>Admins</span>
            <strong>{totalAdmins}</strong>
          </div>

        </section>

        <section className="utilisateurs-toolbar-card">

          <div className="utilisateurs-toolbar-header">
            <div>
              <Typography className="utilisateurs-toolbar-title">
                Recherche et filtres
              </Typography>

              <Typography className="utilisateurs-toolbar-description">
                Filtrez par nom, email, rôle, statut du compte ou accès.
              </Typography>
            </div>

            <Button
              variant="contained"
              className="utilisateurs-primary-btn"
              onClick={() => setOpenAddUser(true)}
            >
              + Ajouter un utilisateur
            </Button>
          </div>

          <div className="utilisateurs-filter-grid">

            <div className="utilisateurs-filter-item utilisateurs-filter-search">
              <Typography className="utilisateurs-filter-label">
                Recherche globale
              </Typography>

              <TextField
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nom, prénom, email..."
                size="small"
                fullWidth
              />
            </div>

            <div className="utilisateurs-filter-item">
              <Typography className="utilisateurs-filter-label">
                Statut
              </Typography>

              <TextField
                select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="Tous">Tous les statuts</MenuItem>
                <MenuItem value="valides">Comptes validés</MenuItem>
                <MenuItem value="non_valides">Non validés</MenuItem>
                <MenuItem value="actifs">Accès actifs</MenuItem>
                <MenuItem value="desactives">Accès désactivés</MenuItem>
              </TextField>
            </div>

            <div className="utilisateurs-filter-item">
              <Typography className="utilisateurs-filter-label">
                Rôle
              </Typography>

              <TextField
                select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="Tous">Tous les rôles</MenuItem>
                <MenuItem value="CDI">CDI</MenuItem>
                <MenuItem value="user">Utilisateur</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
            </div>

            <div className="utilisateurs-filter-actions">
              <Button
                variant="outlined"
                className="utilisateurs-reset-btn"
                onClick={resetFilters}
              >
                Réinitialiser
              </Button>
            </div>

          </div>

          <div className="utilisateurs-result-line">
            {filteredUsers.length} utilisateur(s) affiché(s) sur {users.length}
            {totalActive > 0 ? ` • ${totalActive} actif(s)` : ""}
          </div>

        </section>

        <section className="utilisateurs-table-card">

          <TableContainer className="utilisateurs-table" sx={{ overflowX: "auto" }}>
            <Table>

              <TableHead>
                <TableRow>
                  <TableCell>Utilisateur</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Rôle</TableCell>
                  <TableCell>Mot de passe</TableCell>
                  <TableCell>Accès</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>

                {loading && users.length === 0 && (
                  <>
                    {[1, 2, 3].map((item) => (
                      <TableRow key={item}>
                        <TableCell colSpan={6}>
                          <div className="utilisateurs-skeleton-line"></div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}

                {!loading && filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div className="utilisateurs-empty-state">
                        Aucun utilisateur trouvé.
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {filteredUsers.map((user) => {
                  const valid = isAccountValid(user);
                  const active = isUserActive(user);
                  const userRole = user.role || user.type || user.Type || "—";

                  return (
                    <TableRow
                      key={user.id || user.email}
                      className={!active ? "utilisateurs-row-disabled" : ""}
                    >

                      <TableCell>
                        <div className="utilisateurs-user-cell">
                          <div className="utilisateurs-avatar">
                            {(user.prenom?.[0] || user.nom?.[0] || "?").toUpperCase()}
                          </div>

                          <div>
                            <strong>{user.nom} {user.prenom}</strong>
                            <span>ID : {user.id || "—"}</span>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <span className="utilisateurs-email">{user.email}</span>
                      </TableCell>

                      <TableCell>
                        <span className="role-badge">
                          {getRoleLabel(userRole)}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span className={valid ? "status-badge status-valid" : "status-badge status-pending"}>
                          {valid ? "Compte validé" : user.compte_status || "Compte non validé"}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span className={active ? "status-badge status-valid" : "status-badge status-disabled"}>
                          {active ? "Actif" : "Désactivé"}
                        </span>
                      </TableCell>

                      <TableCell align="right">
                        <div className="utilisateurs-actions-cell">

                          <IconButton
                            className="utilisateurs-edit-btn"
                            onClick={() => openEditDialog(user)}
                            title="Modifier"
                          >
                            <EditIcon />
                          </IconButton>

                          {!valid && (
                            <Button
                              size="small"
                              variant="outlined"
                              className="utilisateurs-mail-btn"
                              startIcon={<MailOutlineIcon />}
                              disabled={resendingEmail === user.email}
                              onClick={() => handleResendPasswordEmail(user)}
                            >
                              {resendingEmail === user.email ? "Envoi..." : "Renvoyer email"}
                            </Button>
                          )}

                          <Button
                            size="small"
                            variant="outlined"
                            className={active ? "utilisateurs-disable-btn" : "utilisateurs-enable-btn"}
                            startIcon={active ? <BlockIcon /> : <CheckCircleIcon />}
                            onClick={() => openToggleDialog(user)}
                          >
                            {active ? "Désactiver" : "Réactiver"}
                          </Button>

                          <IconButton
                            className="utilisateurs-delete-btn"
                            onClick={() => openDeleteDialog(user)}
                            title="Supprimer"
                          >
                            <DeleteIcon />
                          </IconButton>

                        </div>
                      </TableCell>

                    </TableRow>
                  );
                })}

              </TableBody>

            </Table>
          </TableContainer>

        </section>

      </div>

      <Dialog open={openDialog} onClose={() => !deletingUser && setOpenDialog(false)}>
        <DialogTitle>
          Confirmer la suppression
        </DialogTitle>

        <DialogContent>
          <Typography>
            {deletingUser
              ? "Suppression en cours..."
              : `Voulez-vous vraiment supprimer ${userToDelete?.email || "cet utilisateur"} ?`}
          </Typography>

          <Typography sx={{ mt: 1, color: "#b91c1c", fontSize: "14px", fontWeight: 700 }}>
            Attention : supprimer retire l'utilisateur de la base. Pour conserver ses données, utilisez plutôt Désactiver.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            disabled={deletingUser}
          >
            Annuler
          </Button>

          <Button
            color="error"
            onClick={handleDeleteUser}
            disabled={deletingUser}
          >
            {deletingUser ? "Suppression..." : "Supprimer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openActiveDialog}
        onClose={() => !togglingUser && setOpenActiveDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {isUserActive(userToToggle) ? "Désactiver le compte" : "Réactiver le compte"}
        </DialogTitle>

        <DialogContent>
          <Typography>
            {isUserActive(userToToggle)
              ? `Voulez-vous désactiver le compte ${userToToggle?.email || ""} ? Il ne pourra plus se connecter.`
              : `Voulez-vous réactiver le compte ${userToToggle?.email || ""} ? Il pourra de nouveau se connecter.`}
          </Typography>

          <Typography sx={{ mt: 1, color: "#475569", fontSize: "14px" }}>
            Les déclarations, documents et fiches de paie restent conservés.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenActiveDialog(false)}
            disabled={togglingUser}
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            color={isUserActive(userToToggle) ? "warning" : "success"}
            onClick={handleToggleUserActive}
            disabled={togglingUser}
          >
            {togglingUser
              ? "Traitement..."
              : isUserActive(userToToggle)
                ? "Désactiver"
                : "Réactiver"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddUser}
        onClose={() => !creatingUser && setOpenAddUser(false)}
        fullWidth
        maxWidth="sm"
      >

        <DialogTitle>
          Ajouter un utilisateur
        </DialogTitle>

        <DialogContent>

          <TextField
            label="Nom"
            fullWidth
            margin="normal"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
          />

          <TextField
            label="Prénom"
            fullWidth
            margin="normal"
            value={prenom}
            onChange={(e) => setPrenom(e.target.value)}
          />

          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <TextField
            select
            label="Rôle"
            fullWidth
            margin="normal"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <MenuItem value="CDI">CDI</MenuItem>
            <MenuItem value="user">Utilisateur</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>

        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenAddUser(false)}
            disabled={creatingUser}
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={creatingUser}
          >
            {creatingUser ? "Ajout en cours..." : "Ajouter"}
          </Button>
        </DialogActions>

      </Dialog>

      <Dialog
        open={openEditUser}
        onClose={() => !updatingUser && setOpenEditUser(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Modifier l'utilisateur
        </DialogTitle>

        <DialogContent>

          <TextField
            label="Nom"
            fullWidth
            margin="normal"
            value={editNom}
            onChange={(e) => setEditNom(e.target.value)}
          />

          <TextField
            label="Prénom"
            fullWidth
            margin="normal"
            value={editPrenom}
            onChange={(e) => setEditPrenom(e.target.value)}
          />

          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
          />

          <TextField
            select
            label="Rôle"
            fullWidth
            margin="normal"
            value={editRole}
            onChange={(e) => setEditRole(e.target.value)}
          >
            <MenuItem value="CDI">CDI</MenuItem>
            <MenuItem value="user">Utilisateur</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>

          <Typography sx={{ mt: 1, color: "#475569", fontSize: "14px" }}>
            Les déclarations restent liées à l'ID utilisateur. Modifier l'email, le nom, le prénom ou le rôle ne supprime aucune donnée.
          </Typography>

        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenEditUser(false)}
            disabled={updatingUser}
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            onClick={handleUpdateUser}
            disabled={updatingUser}
          >
            {updatingUser ? "Modification..." : "Enregistrer"}
          </Button>
        </DialogActions>

      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={notification.severity}
          sx={{ fontWeight: 'bold' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

    </LayoutDashboard>
  );

};

export default Utilisateurs;